import { generateRequestId } from '@atlassian/mpac-utils';
import algoliasearchHelper from 'algoliasearch-helper';
import { fromJS, Map, Set } from 'immutable';
import FacetType from '../records/FacetType';
import SearchParamsUpdate from '../records/SearchParamsUpdate';
import { resolveFacets, resolveSearchResults } from './algoliaProcessors';
import enhanceFilters from './enhanceFilters';
import resolveSearchParameters from './resolveSearchParametersFromFilters';
import { resolveSearchIndex } from './searchIndex';
import { resolveSortFromFilters } from './sorting';
export function search(_ref) {
  let {
    algoliaClient,
    environment,
    filters,
    numHits,
    page,
    applyOverrides,
    resolveAlgoliaQueryFilterOverrides,
    featureFlags = {}
  } = _ref;
  const enhancedFilters = enhanceFilters(filters, featureFlags);
  const sort = resolveSortFromFilters({
    filters: enhancedFilters
  });
  const {
    enableV2SearchIndex
  } = featureFlags;
  const searchIndexToUse = resolveSearchIndex({
    environment,
    filters,
    sort,
    enableV2SearchIndex
  });
  const searchParameters = resolveSearchParameters({
    applyOverrides,
    filters: enhancedFilters,
    index: searchIndexToUse,
    numHits,
    page,
    sort,
    resolveAlgoliaQueryFilterOverrides,
    featureFlags
  });
  return doSearch({
    algoliaClient,
    filters,
    index: searchIndexToUse,
    searchParameters,
    featureFlags
  });
}
export function getAppDetailsByAppKeys(algoliaClient, environment, appKeys) {
  const searchIndexToUse = resolveSearchIndex({
    environment
  });
  const searchParameters = {
    facetFilters: [appKeys.map(addonKey => "addonKey:".concat(addonKey))]
  };
  return algoliasearchHelper(algoliaClient, searchIndexToUse, {
    hitsPerPage: appKeys.length
  }).searchOnce(searchParameters);
}
function doSearch(_ref2) {
  let {
    algoliaClient,
    filters,
    index,
    searchParameters,
    featureFlags
  } = _ref2;
  return new Promise((resolve, reject) => {
    // This random request id is added to track algolia requests to debug issues faced while upgrading
    // algoliasearch from version 3.35.1 -> 4.19.1 https://ecosystem.atlassian.net/browse/MC-3539
    algoliaClient.setExtraHeader('x-request-id', generateRequestId());
    algoliasearchHelper(algoliaClient, index).searchOnce(searchParameters).then(_ref3 => {
      let {
        content
      } = _ref3;
      resolve(Map({
        facets: resolveFacets({
          facets: fromJS(content.facets).concat(fromJS(content.disjunctiveFacets)),
          filters,
          featureFlags
        }),
        searchResults: resolveSearchResults({
          hits: fromJS(content.hits),
          page: content.page,
          numberOfHits: content.nbHits,
          numberOfPages: content.nbPages
        })
      }));
    }, error => {
      reject(error.name === 'AlgoliaSearchJSONPScriptErrorError' ? {
        message: 'Connection error'
      } : error);
    });
  });
}
export function searchParamsUpdateForAddonKeys(addonKeys) {
  return new SearchParamsUpdate({
    types: Set([FacetType.disjunctive('addonKey')]),
    apply: params => addonKeys.reduce((finalParams, key) => finalParams.addDisjunctiveFacetRefinement('addonKey', key), params)
  });
}
export function searchParamsUpdateForExcludingAddonKeys(addonKeys) {
  return new SearchParamsUpdate({
    types: Set([FacetType.conjunctive('addonKey')]),
    apply: params => addonKeys.reduce((finalParams, key) => finalParams.addExcludeRefinement('addonKey', key), params)
  });
}